/**
 * The subscribe step — the piece M4 built everything else around but never
 * wired up. `usePushStatus.requestPermission()` used to stop the moment the
 * browser prompt resolved, only re-reading the environment; nothing ever
 * called `pushManager.subscribe()`. This module is that missing step.
 *
 * Two entry points, both orchestration around `api/pushSubscriptions.ts`
 * (which this module never re-implements, only calls):
 *
 *   - `subscribeAndPersist` — call `pushManager.subscribe()` against the
 *     current service worker registration and upsert the result. Used both
 *     right after a permission grant (`off` → `on`) and by the `lapsed`
 *     self-heal, which calls this directly: permission is already granted in
 *     that state, so there is no prompt involved and none of this touches
 *     `Notification.requestPermission()`.
 *   - `reconcileOnStart` — call once when the environment reports a
 *     subscription already exists, so a browser-rotated endpoint does not
 *     leave the stored row silently stale. Remembers the last-persisted
 *     endpoint in `localStorage` purely so it can tell `reconcilePushSubscription`
 *     which row to prune; if that memory is unavailable the upsert alone
 *     still keeps the row for the CURRENT endpoint correct, it just cannot
 *     clean up the old one.
 *
 * Kept beside `pushState.ts`'s `readPushEnvironment` philosophy: exactly one
 * place touches these globals (`navigator.serviceWorker`, `localStorage`),
 * everything above takes a plain dependency and is easy to stub.
 *
 * PERSONAL DATA: `endpoint`, `p256dh` and `auth_key` never appear in a
 * returned error string here. Every error is either a caught `Error.message`
 * (DOM/browser errors, which do not carry the endpoint) or
 * `PushSubscriptionResult.error`, which `api/pushSubscriptions.ts` already
 * scrubs down to `PostgrestError.message`.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import {
  base64UrlToUint8Array,
  reconcilePushSubscription,
  removePushSubscription,
  upsertPushSubscription,
} from '../api/pushSubscriptions.ts';

const STORED_ENDPOINT_KEY = 'ns-push-endpoint';

/** Best-effort: losing this only means a future reconcile cannot prune the stale row. */
function getStoredEndpoint(): string | null {
  try {
    return localStorage.getItem(STORED_ENDPOINT_KEY);
  } catch {
    return null;
  }
}

function setStoredEndpoint(endpoint: string): void {
  try {
    localStorage.setItem(STORED_ENDPOINT_KEY, endpoint);
  } catch {
    // best-effort only, see getStoredEndpoint
  }
}

/**
 * Forget this device's endpoint.
 *
 * Needed whenever the endpoint stops being ours to reason about — on logout,
 * and when claiming a device from a previous account. Leaving a stale value
 * behind would make the next reconcile try to prune a row that now belongs to
 * somebody else (invisible under RLS, so a silent no-op) instead of the row it
 * meant to prune.
 */
function clearStoredEndpoint(): void {
  try {
    localStorage.removeItem(STORED_ENDPOINT_KEY);
  } catch {
    // best-effort only, see getStoredEndpoint
  }
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return registration ?? null;
}

/**
 * `base64UrlToUint8Array` is typed as a plain `Uint8Array` (its
 * `ArrayBufferLike` backing is generic enough to include a
 * `SharedArrayBuffer`), while `PushManager.subscribe`'s `applicationServerKey`
 * wants a `BufferSource` backed specifically by a real `ArrayBuffer`. Copying
 * into a freshly allocated `Uint8Array` — never a shared buffer, since we
 * just made it — satisfies that without weakening either type.
 */
function toApplicationServerKey(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return copy;
}

export interface SubscribeOutcome {
  ok: boolean;
  /** Present on failure. Never the endpoint or any subscription field — see module doc. */
  error: string | null;
}

/**
 * Subscribes the current service worker registration to push and persists
 * the row via `upsertPushSubscription`.
 *
 * Never throws: no registration, `subscribe()` rejecting (a bad
 * `applicationServerKey`, the browser refusing, a network failure), a
 * missing encryption key, and the write itself failing all resolve to
 * `{ ok: false, error }` rather than propagating — so a caller can leave the
 * customer looking at an honest state (still `lapsed`, not a false `on`)
 * instead of swallowing the failure into a silent no-op.
 */
export async function subscribeAndPersist(
  client: SupabaseClient,
  portalUserId: string,
  vapidPublicKey: string,
): Promise<SubscribeOutcome> {
  try {
    const registration = await getRegistration();
    if (!registration) {
      return { ok: false, error: 'no service worker registration' };
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: toApplicationServerKey(base64UrlToUint8Array(vapidPublicKey)),
    });

    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    const result = await upsertPushSubscription(client, portalUserId, subscription, userAgent);

    if (result.error) {
      return { ok: false, error: result.error };
    }

    setStoredEndpoint(subscription.endpoint);
    return { ok: true, error: null };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'push subscribe failed' };
  }
}

/**
 * Reconciles the stored row with whatever subscription the browser currently
 * holds — call once on app start when the environment reports a subscription
 * exists. Browsers rotate endpoints silently, so "the stored row is still
 * valid" is not a safe assumption; this is what makes the row follow the
 * browser.
 *
 * A no-op (not an error) when there is no registration or no current
 * subscription — nothing to reconcile against. Failures degrade the same
 * way: this is a best-effort background correction, not something a
 * customer should ever see fail, so a caught error leaves the previously
 * stored row exactly as it was rather than surfacing anything.
 *
 * Idempotent: calling this repeatedly for the same browser subscription
 * upserts the SAME row (`endpoint` is UNIQUE) and, once the stale endpoint
 * has already been pruned, has nothing left to remove — running it twice
 * never creates a second row.
 */
/**
 * Take this device over for the current account, when the browser's
 * subscription still belongs to whoever used the phone before (`foreign`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS CANNOT SIMPLY UPSERT
 *
 * `push_subscriptions.endpoint` is UNIQUE, and `push_subscriptions_own` gates
 * both `using` and `with check` on `portal_user_id = auth.uid()`. So an upsert
 * onto the previous customer's endpoint conflicts with a row this account is
 * not allowed to touch — the write cannot succeed, and `reconcileOnStart`
 * swallows the failure by design. That combination is what left the new
 * customer permanently unreachable on the device while being shown "on".
 *
 * The way out is not to widen the policy — it is to stop fighting over the
 * endpoint. `unsubscribe()` then `subscribe()` makes the BROWSER mint a fresh
 * endpoint, which inserts cleanly: no conflict, no cross-account permission,
 * no schema change. The previous customer's row is left alone and dies on its
 * own — the endpoint it names is now dead, so the next push to it returns 410
 * and the sender's existing prune deletes it (`push-sender.ts`).
 *
 * That the previous customer loses push on this device is the POINT, not a
 * side effect: it is not their device any more, and until now their
 * notifications kept arriving on it.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Requires permission to already be `granted` (the `foreign` state implies it),
 * so this never prompts and needs no user gesture. Never throws.
 */
export async function claimDeviceForCurrentUser(
  client: SupabaseClient,
  portalUserId: string,
  vapidPublicKey: string,
): Promise<SubscribeOutcome> {
  try {
    const registration = await getRegistration();
    if (!registration) {
      return { ok: false, error: 'no service worker registration' };
    }

    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      // Deliberately NOT deleting the row behind it: it belongs to another
      // account and RLS makes it invisible here anyway. Killing the endpoint
      // is enough — the 410 prune finishes the job.
      await existing.unsubscribe();
    }

    // Our own memory of "the endpoint this device used to have" now names a
    // dead endpoint owned by someone else. Clearing it stops the next
    // reconcile from trying to prune a row it cannot see.
    clearStoredEndpoint();

    return await subscribeAndPersist(client, portalUserId, vapidPublicKey);
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'push claim failed' };
  }
}

/**
 * Stop delivering push to this device for the account that is logging out.
 *
 * Product decision (2026-07-31): a customer must NOT keep receiving
 * notifications after logging out. Both halves are required and the ORDER
 * matters — the row is deleted first, while the session is still valid, because
 * RLS resolves `auth.uid()` from that session and a delete attempted after
 * `signOut()` silently affects nothing.
 *
 * Best-effort by construction: logging out must succeed even if the browser or
 * the network refuses, so every failure here is swallowed. The worst case is a
 * stale row, which the 410 prune eventually clears once the endpoint dies.
 */
export async function unsubscribeOnLogout(client: SupabaseClient): Promise<void> {
  try {
    const registration = await getRegistration();
    if (!registration) return;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    await removePushSubscription(client, subscription.endpoint);
    await subscription.unsubscribe();
    clearStoredEndpoint();
  } catch {
    // Logging out is never allowed to fail on this account.
  }
}

export async function reconcileOnStart(client: SupabaseClient, portalUserId: string): Promise<void> {
  try {
    const registration = await getRegistration();
    if (!registration) return;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    const previousEndpoint = getStoredEndpoint();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    const result = await reconcilePushSubscription(
      client,
      portalUserId,
      subscription,
      previousEndpoint,
      userAgent,
    );

    if (!result.error) {
      setStoredEndpoint(subscription.endpoint);
    }
  } catch {
    // best-effort — see module doc.
  }
}
